import React from 'react';
import styled from 'styled-components';
import { useLatchedApiErrorDiagnostics } from '~/hooks/useLatchedApiErrorDiagnostics';
import { theme } from '~/styles/themes';

const ROOT_SEGMENT_PREFIX = 'Root=';

/**
 * Reduces an X-Amzn-Trace-Id header to the root trace id support can paste into
 * X-Ray, e.g. `Root=1-63441c4a-abc;Parent=def;Sampled=1` becomes `1-63441c4a-abc`.
 * Values without a root segment are returned unchanged.
 */
export const getXRayRootTraceId = (traceId?: string): string | undefined => {
  const trimmedTraceId = traceId?.trim();

  if (!trimmedTraceId) {
    return undefined;
  }

  const rootSegment = trimmedTraceId
    .split(';')
    .map((segment) => segment.trim())
    .find((segment) => segment.startsWith(ROOT_SEGMENT_PREFIX));

  if (!rootSegment) {
    return trimmedTraceId;
  }

  return rootSegment.slice(ROOT_SEGMENT_PREFIX.length) || undefined;
};

const TraceId = styled.span`
  display: block;
  margin-top: 0.75em;
  text-align: center;
  font-size: 0.75rem;
  line-height: 1.2;
  color: ${theme.colors.newSecondaryGray};
  word-break: break-all;
`;

// data-record keeps the trace id readable in PostHog session recordings, which mask
// all text by default.
const ErrorTraceId = () => {
  const rootTraceId = getXRayRootTraceId(
    useLatchedApiErrorDiagnostics()?.traceId
  );

  if (!rootTraceId) {
    return null;
  }

  return <TraceId data-record="true">{rootTraceId}</TraceId>;
};

export default ErrorTraceId;
